import React, {ComponentType, memo, useContext, useEffect, useRef, useState} from "react"
import {v4 as uuid} from "uuid"
import {authSlice} from "../slices/authSlice"
import {useRerender} from "../hooks/useRerender"
import {cancelRequest, LastRequests} from "../lib/axios"
import {BodyPortal} from "../features/BodyPortal"
import classNames from "classnames"
import styles from "./styles.module.scss"
import {ModalProps} from "../ui/Modal/Modal"

// Use functional wrapper if you need to pass props from a parent

/**
 * Render modal if only "open" property is true
 * Example:
 interface MyModalProps extends ModalProps {
    myProp: string
 }
 const MyModal: React.FC<MyModalProps> = ({open, onClose, myProp}) => (<div>{myProp}</div>)
 export const WMyModal = modalWrapper<MyModalProps>(MyModal)
 */
export const modalWrapper: <P extends ModalProps = Record<string, unknown> & ModalProps>(
    Component: ComponentType<P>
) => (props: P) => React.ReactElement<P> | null = (Component) => (props) =>
    props.open ? <Component {...props} /> : null

// Use Component wrapper otherwise (You probably need this one)

export interface RenderModalArgs {
    id: string
    open: boolean
    right: boolean
}

const unwrapped = {
    wrapped: false,
}

const wrapped = {
    wrapped: true,
}

const ModalWrapperContext = React.createContext(unwrapped)
export const useModalWrapperContext = () => useContext(ModalWrapperContext)

interface ModalWrapperProps {
    renderModal: (args: RenderModalArgs) => React.ReactElement
    open: boolean
    position: "left" | "right" | "fullscreen" | "bottom" | "center"
    fixVideo?: boolean
    closeActiveRequests?: boolean
    closeRequestsCount?: number
    useDispatch?: () => (param: any) => void
}

const HTMLRenderer: React.FC<{position: string; children: any}> = ({children, position}) => {
    return (
        <BodyPortal>
            <div
                className={classNames(styles.wrapper, styles[position])}
                dangerouslySetInnerHTML={{__html: children}}
            />
        </BodyPortal>
    )
}

export const ModalWrapperFC: React.FC<ModalWrapperProps> = memo(
    ({
        renderModal,
        open,
        position,
        fixVideo = true,
        useDispatch = () => null,
        closeActiveRequests = true,
        closeRequestsCount = 1,
    }) => {
        const timeout = useRef<number>()
        const [id] = useState(() => `modal_${uuid()}`)
        const [isOpenPrevValue, setOpenPrevValue] = useState(open)

        const modalPlaceholder = useRef({position, active: false})
        const dispatch = useDispatch()
        useEffect(() => {
            setOpenPrevValue(open)
            if (open) {
                modalPlaceholder.current.position = position
                if (dispatch) {
                    dispatch(authSlice.actions.resetGlobalUI())
                }
            }
        }, [open])

        const modalRef = useRef<any>(null)
        const rerender = useRerender()

        if (!open && isOpenPrevValue && closeActiveRequests) {
            let closedRequests = 0
            for (let i = LastRequests.items.length - 1; i >= 0; i--) {
                const request = LastRequests.items[i]
                const includes = (str: string) => request.indexOf(str) !== -1
                const ends = (str: string) => request.endsWith(str)
                if (
                    ends("/edit") ||
                    ends("/preview") ||
                    ends("/upload") ||
                    includes("/multipleUpload") ||
                    includes("filterOption") ||
                    includes("available")
                ) {
                    cancelRequest(request)
                    closedRequests++
                    if (closedRequests === closeRequestsCount) break
                }
            }
            clearTimeout(timeout.current)
            modalRef.current = document
                .getElementById(id)
                ?.parentNode?.parentNode?.parentNode?.cloneNode(true)
            if (modalRef.current) {
                const videos = fixVideo ? modalRef.current.getElementsByTagName("video") : []
                for (const video of videos) video.autoplay = false
                modalPlaceholder.current.active = true
                timeout.current = Number(
                    setTimeout(() => {
                        modalPlaceholder.current.active = false
                        for (const video of videos) video.src = ""
                        modalRef.current = undefined
                        rerender()
                    }, 380)
                )
            }
        }

        useEffect(() => {
            return () => clearTimeout(timeout.current)
        }, [])

        return open ? (
            <ModalWrapperContext.Provider value={wrapped}>
                {renderModal({id, open, right: position === "right"})}
            </ModalWrapperContext.Provider>
        ) : modalPlaceholder.current.active ? (
            <HTMLRenderer position={modalPlaceholder.current.position}>
                {modalRef.current.innerHTML}
            </HTMLRenderer>
        ) : null
    }
)
